import React, { useState } from "react";
import { Layout } from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheckIcon,
  TrashIcon,
  ArrowSquareOutIcon,
  SignOutIcon,
  WarningCircleIcon,
  UserCircleIcon,
} from "@phosphor-icons/react";

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);

  const onDeleteAccount = async () => {
    const confirm1 = window.confirm(
      "This will permanently delete your account, all your Instagram connections, keywords, and activity history.\n\nThis cannot be undone. Continue?"
    );
    if (!confirm1) return;
    const typed = window.prompt("Type DELETE to confirm permanent account deletion:");
    if (typed !== "DELETE") {
      toast.info("Deletion cancelled");
      return;
    }
    setDeleting(true);
    try {
      await api.deleteMyAccount();
      toast.success("Your account has been deleted");
      // logout() clears the session cookie
      await logout();
      navigate("/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Deletion failed. Please try again or contact support.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Layout
      title="Settings"
      subtitle="Manage your account, review privacy controls, and delete your data."
    >
      <div className="max-w-2xl space-y-6">
        {/* Account */}
        <section className="rounded-sm border border-zinc-200 bg-white p-6" data-testid="profile-section">
          <div className="mb-2 flex items-center gap-2">
            <UserCircleIcon size={18} weight="fill" className="text-amber-500" />
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Account</p>
          </div>
          <h2 className="font-display text-xl font-bold tracking-tight text-zinc-950">Your profile</h2>
          {user && (
            <div className="mt-4 flex items-center gap-4">
              {user.profile_pic ? (
                <img src={user.profile_pic} alt="" className="h-16 w-16 rounded-full" />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-500 text-2xl font-bold text-zinc-950">
                  {(user.name || "U").slice(0, 1).toUpperCase()}
                </div>
              )}
              <div>
                <p className="font-display text-lg font-bold text-zinc-950" data-testid="profile-name">{user.name}</p>
                <p className="text-sm text-zinc-500" data-testid="profile-email">{user.email || "No email on file"}</p>
              </div>
            </div>
          )}
          <div className="mt-6 flex flex-wrap gap-2">
            <button
              onClick={logout}
              data-testid="settings-logout"
              className="inline-flex items-center gap-2 rounded-sm border border-zinc-200 bg-white px-4 py-2 text-sm font-semibold text-zinc-900 hover:bg-zinc-50"
            >
              <SignOutIcon size={14} weight="bold" /> Sign out
            </button>
          </div>
        </section>

        {/* Privacy & data */}
        <section className="rounded-sm border border-zinc-200 bg-white p-6" data-testid="privacy-section">
          <div className="mb-2 flex items-center gap-2">
            <ShieldCheckIcon size={18} weight="fill" className="text-emerald-500" />
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Privacy & data</p>
          </div>
          <h2 className="font-display text-xl font-bold tracking-tight text-zinc-950">Your rights</h2>
          <p className="mt-2 text-sm text-zinc-500">
            Megatron only stores the minimum data needed to run your automations —
            Instagram comment metadata, your matched keywords, and activity logs. Review
            our full policy or remove all your data at any time.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <a
              href="/privacy"
              target="_blank"
              rel="noreferrer"
              data-testid="settings-privacy-link"
              className="inline-flex items-center gap-2 rounded-sm border border-zinc-200 bg-white px-4 py-2 text-sm font-semibold text-zinc-900 hover:bg-zinc-50"
            >
              <ShieldCheckIcon size={14} weight="duotone" /> Privacy Policy
              <ArrowSquareOutIcon size={12} weight="bold" className="text-zinc-400" />
            </a>
            <a
              href="/data-deletion"
              target="_blank"
              rel="noreferrer"
              data-testid="settings-data-deletion-link"
              className="inline-flex items-center gap-2 rounded-sm border border-zinc-200 bg-white px-4 py-2 text-sm font-semibold text-zinc-900 hover:bg-zinc-50"
            >
              <TrashIcon size={14} weight="duotone" /> Data Deletion Instructions
              <ArrowSquareOutIcon size={12} weight="bold" className="text-zinc-400" />
            </a>
          </div>
        </section>

        {/* Danger zone */}
        <section className="rounded-sm border border-red-200 bg-red-50/50 p-6" data-testid="danger-zone">
          <div className="mb-2 flex items-center gap-2">
            <WarningCircleIcon size={18} weight="fill" className="text-red-600" />
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-red-700">Danger zone</p>
          </div>
          <h2 className="font-display text-xl font-bold tracking-tight text-red-900">Delete my account</h2>
          <p className="mt-2 text-sm text-red-700">
            Permanently delete your Megatron account, all Instagram connections, keywords,
            and activity logs. This action cannot be undone. Instagram webhook subscriptions
            will stop immediately.
          </p>
          <button
            onClick={onDeleteAccount}
            disabled={deleting}
            data-testid="delete-account-btn"
            className="mt-4 inline-flex items-center gap-2 rounded-sm bg-red-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-60"
          >
            <TrashIcon size={14} weight="bold" />
            {deleting ? "Deleting…" : "Delete my account"}
          </button>
        </section>
      </div>
    </Layout>
  );
}
